import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Check, Upload } from "lucide-react";

import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

export interface StyledFileInputProps {
  id: string;
  accept: string;
  value: File | null;
  onChange: (file: File | null) => void;
  disabled?: boolean;
  label?: string;
  /** Merged with default label styles (field caption vs page title). */
  labelClassName?: string;
  hint?: string;
  placeholder?: string;
  className?: string;
  /** Extra classes for the clickable dropzone (border area). */
  dropzoneClassName?: string;
}

export function StyledFileInput({
  id,
  accept,
  value,
  onChange,
  disabled = false,
  label,
  labelClassName,
  hint,
  placeholder,
  className,
  dropzoneClassName,
}: StyledFileInputProps): JSX.Element {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);

  const triggerInput = useCallback(() => {
    if (!disabled) {
      inputRef.current?.click();
    }
  }, [disabled]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      onChange(file);
    },
    [onChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        triggerInput();
      }
    },
    [triggerInput]
  );

  const handleChangeClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!disabled) {
        onChange(null);
        if (inputRef.current) {
          inputRef.current.value = "";
        }
        inputRef.current?.click();
      }
    },
    [disabled, onChange]
  );

  const chooseLabel = placeholder ?? t("components.styledFileInput.noFileChosen");
  const changeLabel = t("components.styledFileInput.changeFile");

  const hintId = hint ? `${id}-hint` : undefined;

  return (
    <div className={cn("space-y-2", className)}>
      {label != null && (
        <Label
          htmlFor={id}
          className={cn("text-sm font-medium text-muted-foreground", labelClassName)}
        >
          {label}
        </Label>
      )}
      <input
        ref={inputRef}
        id={id}
        type="file"
        accept={accept}
        className="sr-only"
        disabled={disabled}
        onChange={handleChange}
        aria-describedby={hintId}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={triggerInput}
        onKeyDown={handleKeyDown}
        aria-label={value ? value.name : chooseLabel}
        className={cn(
          // Base dropzone (empty state): dashed neutral border + muted bg.
          //
          // Dark mode is not the same recipe at lower brightness. There
          // `--card` and `--muted` are the SAME value (#1f2937), so a muted
          // tint over a card surface leaves the field and the thing it sits on
          // within a hair of each other and only the dashed border says a
          // control is there — at border/90 it barely did. The field is
          // recessed onto `--background` instead, which is how every other
          // input in the app reads (see ui/input.tsx), with the border at full
          // strength.
          "flex min-h-[2.5rem] min-w-0 cursor-pointer items-center gap-3 overflow-hidden rounded-md border border-dashed border-border/90 bg-muted/20 px-3 py-2.5 shadow-sm transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none dark:border-border dark:bg-background/70",
          // Filled state: solid green border + subtle green tint to mirror
          // the mobile upload tile — scanning a list of slots makes "done
          // vs. not done" obvious at a glance.
          value
            ? "border-solid border-green-300 bg-green-50 hover:border-green-400 hover:bg-green-100/70 dark:border-green-800/80 dark:bg-green-950/40 dark:hover:bg-green-950/60"
            : "hover:border-primary/45 hover:bg-muted/30 dark:hover:border-primary/60 dark:hover:bg-background",
          disabled && "cursor-not-allowed opacity-70",
          dropzoneClassName,
        )}
      >
        {value ? (
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-100 dark:bg-green-900/50">
            <Check className="h-4 w-4 text-green-700 dark:text-green-300" />
          </div>
        ) : (
          <Upload className="h-5 w-5 shrink-0 text-muted-foreground" />
        )}
        <div className="min-w-0 flex-1">
          {value ? (
            <div className="flex min-w-0 items-center justify-between gap-2">
              <span className="min-w-0 truncate text-sm font-medium text-foreground" title={value.name}>
                {value.name}
              </span>
              <button
                type="button"
                onClick={handleChangeClick}
                disabled={disabled}
                className="shrink-0 text-xs text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
              >
                {changeLabel}
              </button>
            </div>
          ) : (
            <>
              <p className="text-sm font-medium text-foreground">
                {t("components.styledFileInput.chooseFile")}
              </p>
              {hint != null && (
                <p id={hintId} className="text-xs text-muted-foreground">
                  {hint}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
